import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const LetterCard = ({ letter, onDownload, onViewDetails }) => {
  const [downloading, setDownloading] = useState(false);

  const statusConfig = {
    draft: {
      label: 'Draft',
      color: 'bg-muted text-muted-foreground',
      icon: 'FileEdit',
    },
    pending_approval: {
      label: 'Pending Approval',
      color: 'bg-warning/10 text-warning',
      icon: 'Clock',
    },
    approved: {
      label: 'Approved',
      color: 'bg-success/10 text-success',
      icon: 'CheckCircle2',
    },
    rejected: {
      label: 'Rejected',
      color: 'bg-error/10 text-error',
      icon: 'XCircle',
    },
    downloaded: {
      label: 'Downloaded',
      color: 'bg-primary/10 text-primary',
      icon: 'Download',
    },
  };

  const purposeConfig = {
    banking: {
      label: 'Banking Purposes',
      icon: 'Building2',
      color: 'text-blue-600',
    },
    superannuation_withdrawal: {
      label: 'Superannuation Withdrawal',
      icon: 'Wallet',
      color: 'text-purple-600',
    },
    staff_reference: {
      label: 'Staff Reference',
      icon: 'FileText',
      color: 'text-green-600',
    },
  };

  const status = statusConfig?.[letter?.letterStatus] || statusConfig?.draft;
  const purpose = purposeConfig?.[letter?.letterPurpose] || purposeConfig?.banking;

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await onDownload?.(letter);
    } finally {
      setDownloading(false);
    }
  };

  const canDownload = letter?.letterStatus === 'approved' || letter?.letterStatus === 'downloaded';

  return (
    <div className="bg-card border border-border rounded-lg p-5 hover:shadow-md transition-smooth">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <Icon name={purpose?.icon} size={18} className={purpose?.color} />
            <h4 className="font-semibold text-foreground">{purpose?.label}</h4>
          </div>
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <span>
              <Icon name="Calendar" size={14} className="inline mr-1" />
              {new Date(letter?.createdAt)?.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })}
            </span>
            {letter?.downloadCount > 0 && (
              <span>
                <Icon name="Download" size={14} className="inline mr-1" />
                Downloaded {letter?.downloadCount} time{letter?.downloadCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>
        <div className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1.5 ${status?.color}`}>
          <Icon name={status?.icon} size={14} />
          {status?.label}
        </div>
      </div>

      {/* Status-specific information */}
      {letter?.letterStatus === 'pending_approval' && (
        <div className="bg-warning/5 border border-warning/20 rounded-lg p-3 mb-4">
          <div className="flex gap-2">
            <Icon name="Clock" size={16} className="text-warning flex-shrink-0 mt-0.5" />
            <p className="text-sm text-foreground">
              Your letter is awaiting HR Manager approval. You will be notified once it is ready.
            </p>
          </div>
        </div>
      )}

      {letter?.letterStatus === 'approved' && !letter?.downloadedAt && (
        <div className="bg-success/5 border border-success/20 rounded-lg p-3 mb-4">
          <div className="flex gap-2">
            <Icon name="CheckCircle2" size={16} className="text-success flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-foreground mb-1">Letter Approved!</p>
              <p className="text-muted-foreground">
                Signed by {letter?.hrSignatureName} on{' '}
                {new Date(letter?.hrSignatureDate)?.toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      )}

      {letter?.letterStatus === 'rejected' && (
        <div className="bg-error/5 border border-error/20 rounded-lg p-3 mb-4">
          <div className="flex gap-2">
            <Icon name="XCircle" size={16} className="text-error flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-foreground mb-1">Letter Rejected</p>
              <p className="text-muted-foreground">{letter?.rejectionReason || 'No reason provided'}</p>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          iconName="Eye"
          onClick={() => onViewDetails?.(letter)}
          className="flex-1"
        >
          View Details
        </Button>
        {canDownload && (
          <Button
            variant="default"
            size="sm"
            iconName="Download"
            onClick={handleDownload}
            disabled={downloading}
            className="flex-1"
          >
            {downloading ? 'Downloading...' : 'Download PDF'}
          </Button>
        )}
      </div>
    </div>
  );
};

export default LetterCard;